import {
    TooltipCordType,
    TooltipDotSizeType,
    TooltipFootnoteSizeTextType,
    TooltipInURLSizeType,
    TooltipSizeLineCoordsType,
    TooltipSizeType,
    TooltipSizeVariantInURLType,
    TooltipSizeVariantType,
    TooltipTwoLinesSizeInURLType,
    TooltipTwoLinesSizesPlacementType,
    TooltipTwoLinesSizeTextType,
    TooltipTwoLinesSizeType,
} from './types';
import { connectingLineProtrusionLength } from './constants';

const isSizeTwoLineVariant = (size: TooltipSizeType): size is TooltipTwoLinesSizeType =>
    size.variant === TooltipSizeVariantType.TwoLines;

const isSizeInURLTwoLineVariant = (
    size: TooltipInURLSizeType,
): size is TooltipTwoLinesSizeInURLType => size[0] === TooltipSizeVariantInURLType.TwoLines;

const getCoordsInPixels = (x: number, y: number, canvas: HTMLCanvasElement): TooltipCordType => ({
    x: (x / 100) * canvas.width,
    y: (y / 100) * canvas.height,
});

const getLineCoords = (
    canvas: HTMLCanvasElement,
    position: TooltipTwoLinesSizesPlacementType,
    pointCoords: TooltipCordType,
    sizeDisplayCoords: TooltipCordType,
): TooltipSizeLineCoordsType => {
    const preparedPointCoords = getCoordsInPixels(pointCoords.x, pointCoords.y, canvas);
    let secondPointCoords: TooltipCordType;

    if (position === TooltipTwoLinesSizesPlacementType.Horizontal) {
        secondPointCoords = getCoordsInPixels(pointCoords.x, sizeDisplayCoords.y, canvas);
    } else {
        secondPointCoords = getCoordsInPixels(sizeDisplayCoords.x, pointCoords.y, canvas);
    }

    return [preparedPointCoords, secondPointCoords];
};

const drawConnectingLine = (
    ctx: CanvasRenderingContext2D,
    coords: [TooltipCordType, TooltipCordType],
    position: TooltipTwoLinesSizesPlacementType,
    isOuter: boolean = false,
) => {
    ctx.beginPath();

    if (position === TooltipTwoLinesSizesPlacementType.Horizontal) {
        ctx.moveTo(coords[0].x - (isOuter ? connectingLineProtrusionLength : 0), coords[0].y);
        ctx.lineTo(coords[1].x + (isOuter ? connectingLineProtrusionLength : 0), coords[1].y);
    } else {
        ctx.moveTo(coords[0].x, coords[0].y - (isOuter ? connectingLineProtrusionLength : 0));
        ctx.lineTo(coords[1].x, coords[1].y + (isOuter ? connectingLineProtrusionLength : 0));
    }

    ctx.stroke();
};

const drawTriangles = (
    ctx: CanvasRenderingContext2D,
    position: TooltipTwoLinesSizesPlacementType,
    points: [TooltipCordType, TooltipCordType],
    isOuter: boolean = false,
): void => {
    const lengthOfHeightOfTriangle = ctx.canvas.height * 0.015;
    const lengthOfBaseOfTriangle = lengthOfHeightOfTriangle * 0.77;

    ctx.beginPath();

    if (position === TooltipTwoLinesSizesPlacementType.Horizontal) {
        const [leftPoint, rightPoint] = points[0].x < points[1].x ? points : points.reverse();

        ctx.moveTo(leftPoint.x, leftPoint.y);
        ctx.lineTo(
            leftPoint.x + lengthOfHeightOfTriangle * (isOuter ? -1 : 1),
            leftPoint.y + lengthOfBaseOfTriangle,
        );
        ctx.lineTo(
            leftPoint.x + lengthOfHeightOfTriangle * (isOuter ? -1 : 1),
            leftPoint.y - lengthOfBaseOfTriangle,
        );

        ctx.moveTo(rightPoint.x, rightPoint.y);
        ctx.lineTo(
            rightPoint.x - lengthOfHeightOfTriangle * (isOuter ? -1 : 1),
            rightPoint.y + lengthOfBaseOfTriangle,
        );
        ctx.lineTo(
            rightPoint.x - lengthOfHeightOfTriangle * (isOuter ? -1 : 1),
            rightPoint.y - lengthOfBaseOfTriangle,
        );
    } else {
        const [topPoint, bottomPoint] = points[0].y > points[1].y ? points : points.reverse();

        ctx.moveTo(topPoint.x, topPoint.y);
        ctx.lineTo(
            topPoint.x + lengthOfBaseOfTriangle,
            topPoint.y - lengthOfHeightOfTriangle * (isOuter ? -1 : 1),
        );
        ctx.lineTo(
            topPoint.x - lengthOfBaseOfTriangle,
            topPoint.y - lengthOfHeightOfTriangle * (isOuter ? -1 : 1),
        );

        ctx.moveTo(bottomPoint.x, bottomPoint.y);
        ctx.lineTo(
            bottomPoint.x + lengthOfBaseOfTriangle,
            bottomPoint.y + lengthOfHeightOfTriangle * (isOuter ? -1 : 1),
        );
        ctx.lineTo(
            bottomPoint.x - lengthOfBaseOfTriangle,
            bottomPoint.y + lengthOfHeightOfTriangle * (isOuter ? -1 : 1),
        );
    }

    ctx.fillStyle = 'red';
    ctx.fill();
};

const drawFootnote = (
    canvas: HTMLCanvasElement,
    startPointCord: TooltipCordType,
    targetPointCord: TooltipCordType,
    text: TooltipFootnoteSizeTextType,
    withCirce: boolean = false,
) => {
    const ctx = canvas.getContext('2d');

    if (ctx) {
        const fontSize = ctx.canvas.height * 0.05;
        const indentTextFromTheLine = fontSize * 0.4;

        ctx.beginPath();

        const topTextWidth = ctx.measureText(text.top || '').width;
        const bottomTextWidth = ctx.measureText(text.bottom || '').width;
        const maxTextWidth = Math.max(topTextWidth, bottomTextWidth);
        const halfLengthOfTextLine = (maxTextWidth + indentTextFromTheLine * 2) / 2;

        let topTextCoords: TooltipCordType;
        let bottomTextCoords: TooltipCordType;

        const isFootnoteXTargetPointCordBiggerThanStart = targetPointCord.x > startPointCord.x;
        let endOfFootnoteCord: TooltipCordType;

        if (isFootnoteXTargetPointCordBiggerThanStart) {
            endOfFootnoteCord = {
                x: targetPointCord.x + halfLengthOfTextLine * 2,
                y: targetPointCord.y,
            };

            topTextCoords = {
                x: targetPointCord.x + halfLengthOfTextLine - topTextWidth / 2,
                y: targetPointCord.y - indentTextFromTheLine,
            };
            bottomTextCoords = {
                x: targetPointCord.x + halfLengthOfTextLine - bottomTextWidth / 2,
                y: targetPointCord.y + fontSize,
            };
        } else {
            endOfFootnoteCord = {
                x: targetPointCord.x - halfLengthOfTextLine * 2,
                y: targetPointCord.y,
            };

            topTextCoords = {
                x: endOfFootnoteCord.x + halfLengthOfTextLine - topTextWidth / 2,
                y: targetPointCord.y - indentTextFromTheLine,
            };
            bottomTextCoords = {
                x: endOfFootnoteCord.x + halfLengthOfTextLine - bottomTextWidth / 2,
                y: targetPointCord.y + fontSize,
            };
        }

        if (withCirce) {
            ctx.arc(startPointCord.x, startPointCord.y, 3, 0, 2 * Math.PI);
            ctx.fillStyle = 'red';
            ctx.fill();
        }

        ctx.moveTo(startPointCord.x, startPointCord.y);
        ctx.lineTo(targetPointCord.x, targetPointCord.y);
        ctx.lineTo(endOfFootnoteCord.x, endOfFootnoteCord.y);
        ctx.lineWidth = canvas.height * 0.005;
        ctx.strokeStyle = 'red';
        ctx.stroke();

        ctx.font = `${fontSize}px serif`;
        ctx.textAlign = 'left';
        text.top && ctx.fillText(text.top, topTextCoords.x, topTextCoords.y);
        text.bottom && ctx.fillText(text.bottom, bottomTextCoords.x, bottomTextCoords.y);
    }
};

const drawText = (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    lineCoords: TooltipSizeLineCoordsType,
    position: TooltipTwoLinesSizesPlacementType,
    text: TooltipTwoLinesSizeTextType,
) => {
    const footnoteStartPointCord = text.footnoteStartPointCord;

    const lineHalfWidth = (lineCoords[1].x - lineCoords[0].x) / 2;
    const lineHalfHeight = (lineCoords[1].y - lineCoords[0].y) / 2;
    const lineCenterCord: TooltipCordType = {
        x: lineCoords[0].x + lineHalfWidth,
        y: lineCoords[0].y + lineHalfHeight,
    };

    if (footnoteStartPointCord) {
        const preparedFootnoteTargetPointCord = getCoordsInPixels(
            footnoteStartPointCord.x,
            footnoteStartPointCord.y,
            canvas,
        );

        drawFootnote(canvas, lineCenterCord, preparedFootnoteTargetPointCord, { top: text.text });
    } else {
        const fontSize = ctx.canvas.height * 0.05;
        const indentTextFromTheLine = fontSize * 0.4;

        ctx.beginPath();

        const textWidth = ctx.measureText(text.text).width;
        let textCoords: TooltipCordType;

        if (position === TooltipTwoLinesSizesPlacementType.Horizontal) {
            textCoords = {
                x: lineCenterCord.x - textWidth / 2,
                y: lineCoords[0].y - indentTextFromTheLine,
            };
        } else {
            textCoords = {
                x: lineCoords[1].x + indentTextFromTheLine,
                y: lineCenterCord.y + fontSize / 4,
            };
        }

        ctx.fillStyle = 'red';
        ctx.font = `${ctx.canvas.height * 0.05}px serif`;
        ctx.textAlign = 'left';

        ctx.fillText(text.text, textCoords.x, textCoords.y);
    }
};

const drawTwoLineVariantSize = (
    canvas: HTMLCanvasElement,
    { position, pointCoords, sizeDisplayCoords, text }: TooltipTwoLinesSizeType,
) => {
    const ctx = canvas.getContext('2d');

    if (ctx) {
        const firstLineCoords = getLineCoords(canvas, position, pointCoords[0], sizeDisplayCoords);
        const secondLineCoords = getLineCoords(canvas, position, pointCoords[1], sizeDisplayCoords);
        const connectingLineCoords: TooltipSizeLineCoordsType = [
            { x: firstLineCoords[1].x, y: firstLineCoords[1].y },
            { x: secondLineCoords[1].x, y: secondLineCoords[1].y },
        ];

        const isTextOutsideLines = !!text?.footnoteStartPointCord;

        ctx.beginPath();
        ctx.lineWidth = canvas.height * 0.005;
        ctx.strokeStyle = 'red';
        ctx.moveTo(firstLineCoords[0].x, firstLineCoords[0].y);
        ctx.lineTo(firstLineCoords[1].x, firstLineCoords[1].y);
        ctx.moveTo(secondLineCoords[0].x, secondLineCoords[0].y);
        ctx.lineTo(secondLineCoords[1].x, secondLineCoords[1].y);
        ctx.stroke();

        drawConnectingLine(ctx, connectingLineCoords, position, isTextOutsideLines);

        drawTriangles(ctx, position, [firstLineCoords[1], secondLineCoords[1]], isTextOutsideLines);

        if (text) drawText(canvas, ctx, connectingLineCoords, position, text);
    }
};

const drawDotVariantSize = (
    canvas: HTMLCanvasElement,
    { objectCoords, sizeDisplayCoords, text }: TooltipDotSizeType,
) => {
    const ctx = canvas.getContext('2d');

    const preparedObjectCoords = getCoordsInPixels(objectCoords.x, objectCoords.y, canvas);
    const preparedSizeDisplayCoords = getCoordsInPixels(
        sizeDisplayCoords.x,
        sizeDisplayCoords.y,
        canvas,
    );

    if (ctx) drawFootnote(canvas, preparedObjectCoords, preparedSizeDisplayCoords, text, true);
};

const drawSizes = (canvas: HTMLCanvasElement, sizes: TooltipSizeType[]) => {
    const ctx = canvas.getContext('2d');

    if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        sizes.forEach(size => {
            if (isSizeTwoLineVariant(size)) {
                drawTwoLineVariantSize(canvas, size);
            } else {
                drawDotVariantSize(canvas, size);
            }
        });
    }
};

export { drawSizes, isSizeTwoLineVariant, isSizeInURLTwoLineVariant };
